import { collection, addDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '../firebase/config'

export const PENDING_SCORES_KEY = 'pendingScoresQueue'
export const CACHED_LEADERBOARD_KEY = 'cachedLeaderboard'

let isFlushInProgress = false
let hasRegisteredSyncListeners = false

const hasBrowserEnv = () => typeof window !== 'undefined'
const hasDocumentEnv = () => typeof document !== 'undefined'

const safeParseJSON = (value, fallback = []) => {
  try {
    return value ? JSON.parse(value) : fallback
  } catch (error) {
    console.error('Failed to parse JSON from localStorage', error)
    return fallback
  }
}

const writePendingScores = (queue) => {
  if (!hasBrowserEnv()) return
  try {
    window.localStorage.setItem(PENDING_SCORES_KEY, JSON.stringify(queue))
  } catch (error) {
    console.error('Failed to persist pending scores queue', error)
  }
}

const cacheScoreForLeaderboard = (payload, { isPending } = { isPending: true }) => {
  if (!hasBrowserEnv()) return

  const cached = safeParseJSON(window.localStorage.getItem(CACHED_LEADERBOARD_KEY), [])

  const sanitizedEntry = {
    id: payload.clientId || payload.id || `temp-${Date.now()}`,
    clientId: payload.clientId,
    score: payload.score,
    firstName: payload.firstName || '',
    lastName: payload.lastName || '',
    initials: payload.initials || `${payload.firstName || ''}${payload.lastName || ''}`.trim(),
    isPending,
    timestamp: payload.createdAt || new Date().toISOString(),
  }

  const next = [
    ...cached.filter((entry) => entry.clientId !== sanitizedEntry.clientId),
    sanitizedEntry,
  ]
    .sort((a, b) => b.score - a.score)
    .slice(0, 10)

  window.localStorage.setItem(CACHED_LEADERBOARD_KEY, JSON.stringify(next))
}

export const normalizePhoneValue = (value = '') => value.replace(/\s+/g, ' ').trim()
export const extractDigits = (value = '') => value.replace(/\D/g, '')

export const readPendingScores = () => {
  if (!hasBrowserEnv()) return []
  return safeParseJSON(window.localStorage.getItem(PENDING_SCORES_KEY), [])
}

export const generateScoreClientId = () => {
  const globalCrypto = typeof crypto !== 'undefined' ? crypto : undefined
  const windowCrypto = hasBrowserEnv() ? window.crypto : undefined
  const cryptoSource = windowCrypto || globalCrypto

  if (cryptoSource && typeof cryptoSource.randomUUID === 'function') {
    return cryptoSource.randomUUID()
  }

  return `score-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

export const enqueuePendingScore = (payload) => {
  if (!hasBrowserEnv()) return null
  const normalizedPayload = payload.clientId ? payload : { ...payload, clientId: generateScoreClientId() }
  const queue = readPendingScores()
  queue.push(normalizedPayload)
  writePendingScores(queue)
  cacheScoreForLeaderboard(normalizedPayload, { isPending: true })
  return normalizedPayload.clientId
}

export const flushPendingScores = async () => {
  if (!hasBrowserEnv()) return
  if (typeof navigator !== 'undefined' && !navigator.onLine) return
  if (isFlushInProgress) return

  const pending = readPendingScores()
  if (!pending.length) return

  isFlushInProgress = true
  const scoresRef = collection(db, 'scores')
  const stillPending = []

  try {
    for (const payload of pending) {
      try {
        await addDoc(scoresRef, {
          ...payload,
          timestamp: serverTimestamp(),
          syncedAt: serverTimestamp(),
        })

        cacheScoreForLeaderboard(payload, { isPending: false })
      } catch (error) {
        console.error('Failed to sync queued score', error)
        stillPending.push(payload)
      }
    }
  } finally {
    writePendingScores(stillPending)
    isFlushInProgress = false
  }
}

const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    flushPendingScores()
  }
}

export const registerScoreSyncListener = () => {
  if (!hasBrowserEnv() || hasRegisteredSyncListeners) return

  window.addEventListener('online', flushPendingScores)

  if (hasDocumentEnv()) {
    document.addEventListener('visibilitychange', handleVisibilityChange)
  }

  hasRegisteredSyncListeners = true
}

export const startPeriodicFlush = () => {
  if (!hasBrowserEnv()) return () => {}
  const intervalId = window.setInterval(flushPendingScores, 60 * 1000)
  return () => clearInterval(intervalId)
}

